<template>
  <div>
    <div :class="containerClass" class="d-flex justify-content-between mt-3">
      <h2>
        View Assignments
        <i class="material-icons icon-16pt clickable-item" v-b-popover.hover.top="'View and manage assignments.'">
          info
        </i>
      </h2>
      <div>
        <b-btn size="md" variant="primary" :to="{ name: 'create-assignment' }">
          Add Assignment
        </b-btn>
      </div>
    </div>

    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt" @submit.prevent="">
                  <b-form-input
                    class="w-lg-auto"
                    placeholder="Search Assignments"
                    v-model="searchTerm"
                    @input="onSearch"
                  />
                  <b-btn variant="flush" type="submit">
                    <md-icon>search</md-icon>
                  </b-btn>
                </form>
              </div>
            </div>

            <b-table
              :fields="tableColumns"
              :items="assignments"
              v-if="assignments.length"
              :busy="isLoading"
              head-variant="light"
              class="table-nowrap"
              hover
              responsive
              no-local-sorting
              @sort-changed="onSortChange"
              @row-clicked="rowClicked"
            >
              <template #table-busy>
                <div class="text-center my-2">
                  <b-spinner class="align-middle mr-2"></b-spinner>
                  <strong>Loading...</strong>
                </div>
              </template>

              <template #cell(title)="data">
                <strong>{{ data.item.title }}</strong>
              </template>

              <template #cell(description)="data">
                <div class="text-truncate" style="max-width: 300px">
                  {{
                    data.item.description.length > 100
                      ? data.item.description.substring(0, 100) + '...'
                      : data.item.description
                  }}
                </div>
              </template>

              <template #cell(actions)="data">
                <router-link
                  :to="{
                    name: 'edit-assignment',
                    params: { id: data.item.id },
                  }"
                  class="svg-icon mr-2"
                  v-b-popover.hover.left
                  title="Edit Assignment"
                >
                  <md-icon class="text-info">edit</md-icon>
                </router-link>

                <a
                  href="#"
                  @click.prevent="confirmDeleteAssignment(data.item.id)"
                  v-b-popover.hover.left
                  title="Delete Assignment"
                >
                  <md-icon class="text-danger">delete</md-icon>
                </a>
              </template>
            </b-table>

            <div v-if="!isLoading && !assignments.length" class="text-center p-4">
              <p>No assignments found.</p>
            </div>

            <div class="card-footer" v-if="totalAssignments > perPage">
              <pagination
                v-model="currentPage"
                :total-rows="totalAssignments"
                :per-page="perPage"
                @change="onPageChange"
                aria-controls="assignments-table"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import Page from '@/components/Page.vue';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants';
import Pagination from '../../../components/Ui/Pagination.vue';

export default {
  components: {
    Pagination,
  },
  extends: Page,

  data() {
    return {
      title: 'View Assignments',
      isLoading: true,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalAssignments: 0,
      searchTerm: '',
      sortBy: 'title',
      sortDesc: false,
      assignments: [],
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    tableColumns() {
      return [
        { key: 'title', label: 'Title', sortable: true },
        { key: 'description', label: 'Description', sortable: false },
        { key: 'total_tasks', label: 'Tasks', sortable: false, class: 'text-center' },
        { key: 'actions', label: 'Actions', class: 'text-right', sortable: false },
      ];
    },
  },

  methods: {
    ...mapActions('assignments', ['getAssignments', 'deleteAssignment']),

    onPageChange(pageNum) {
      this.currentPage = pageNum;
      this.fetchAssignments();
    },

    async confirmDeleteAssignment(id) {
      const confirmed = await this.$bvModal.msgBoxConfirm('Are you sure you want to delete this assignment?', {
        title: 'Confirm Delete',
        okVariant: 'danger',
        okTitle: 'Delete',
        cancelTitle: 'Cancel',
        centered: true,
      });

      if (confirmed) {
        await this.onDeleteAssignment(id);
      }
    },

    async onDeleteAssignment(id) {
      try {
        await this.deleteAssignment(id);
        this.makeToast({ variant: 'success', msg: 'Assignment deleted successfully' });
        this.fetchAssignments();
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Failed to delete assignment' });
      }
    },

    async fetchAssignments() {
      this.isLoading = true;
      try {
        const params = {
          page: this.currentPage,
          page_size: this.perPage,
          search: this.searchTerm,
          ordering: this.sortDesc ? `-${this.sortBy}` : this.sortBy,
        };

        const response = await this.getAssignments(params);
        this.assignments = response.data.results ? response.data.results : response.data;
        this.totalAssignments = response.data.count || response.data.length;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Failed to fetch assignments' });
      } finally {
        this.isLoading = false;
      }
    },

    rowClicked(row) {
      this.$router.push({
        name: 'view-assignment',
        params: { id: row.id },
      });
    },

    onSortChange(context) {
      this.sortBy = context.sortBy;
      this.sortDesc = context.sortDesc;
      this.fetchAssignments();
    },

    onSearch() {
      this.currentPage = 1;
      this.debouncedAssignmentSearch();
    },

    debouncedAssignmentSearch: debounce(function() {
      this.fetchAssignments();
    }, 500),
  },

  created() {
    this.fetchAssignments();
  },
};
</script>

<style scoped>
.clickable-item {
  cursor: pointer;
}
</style>
